import * as React from 'react';

import { IconProps, Loading02Icon } from '@/components/icons/Loading02Icon';

export interface SpinningIconProps extends IconProps {
  Icon?: React.ElementType;
  speed?: number;
  unit?: 's' | 'ms';
}

/**
 * A wrapper around an icon that spins it.
 * @param Icon
 * @param iconProps
 * @param speed
 * @param unit
 */
export const SpinningIcon = ({
  Icon = Loading02Icon,
  speed = 2,
  unit = 's',
  ...iconProps
}: SpinningIconProps) => {
  const spinningStyles = React.useMemo(
    () => ({
      animation: `spin ${speed}${unit} linear infinite`,
      '@keyframes spin': {
        from: { transform: 'rotate(0deg)' },
        to: { transform: 'rotate(360deg)' },
      },
      // Icons render their inner <svg> as an inline element, so it aligns to the
      // text baseline and sits ~1px above the container's center. The spin
      // pivots around the container's center, so that offset makes the icon
      // visibly wobble. Forcing the svg to a block element removes the baseline
      // gap and centers it in the container so it spins in place.
      '& > svg': {
        display: 'block',
      },
    }),
    [speed, unit]
  );

  return <Icon {...iconProps} sx={{ ...iconProps?.sx, ...spinningStyles }} />;
};
